/**
 * Online Food Ordering Cookie Consent Banner
 * GDPR compliant cookie consent with granular controls
 */

import {
  ExpandLess as CollapseIcon,
  Cookie as CookieIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';

import { useAppStore } from '@/store/useAppStore';
import type { CookieConsent } from '@/types';

export default function CookieConsentBanner() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const setCookieConsent = useAppStore((state) => state.setCookieConsent);

  const [expanded, setExpanded] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    essential: true, // Always required
    analytics: false,
    marketing: false,
  });

  const handleAcceptAll = () => {
    setCookieConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleAcceptSelected = () => {
    setCookieConsent(consent);
  };

  const handleRejectAll = () => {
    setCookieConsent({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: 'spring', damping: 25 }}
    >
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: { xs: 56, sm: 16 }, // Account for mobile nav on xs, add margin on larger
          left: { xs: 0, sm: '50%' },
          right: { xs: 0, sm: 'auto' },
          transform: { xs: 'none', sm: 'translateX(-50%)' },
          width: { xs: '100%', sm: '90%', md: '600px' },
          maxWidth: { sm: '600px' },
          zIndex: theme.zIndex.modal + 1,
          borderRadius: { xs: '20px 20px 0 0', sm: 3 },
          overflow: 'hidden',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CookieIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Cookie Preferences
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                We use cookies to improve your experience
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            We use cookies to enhance your browsing experience, serve personalised content, and
            analyse our traffic. By clicking "Accept All", you consent to our use of cookies.{' '}
            <Link href="/cookies" color="primary">
              Learn more
            </Link>
          </Typography>

          {/* Expandable Settings */}
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <CollapseIcon /> : <ExpandIcon />}
            sx={{ mb: 2 }}
          >
            {expanded ? 'Hide Settings' : 'Cookie Settings'}
          </Button>

          <Collapse in={expanded}>
            <Box
              sx={{
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                p: 2,
                mb: 2,
              }}
            >
              {/* Essential Cookies */}
              <FormControlLabel
                control={<Switch checked disabled />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Essential Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Required for the website to function properly
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 2 }}
              />

              <Divider sx={{ my: 1.5 }} />

              {/* Analytics Cookies */}
              <FormControlLabel
                control={
                  <Switch
                    checked={consent.analytics}
                    onChange={(e) =>
                      setConsent((prev) => ({ ...prev, analytics: e.target.checked }))
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Analytics Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Help us understand how visitors interact with our website
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 2 }}
              />

              <Divider sx={{ my: 1.5 }} />

              {/* Marketing Cookies */}
              <FormControlLabel
                control={
                  <Switch
                    checked={consent.marketing}
                    onChange={(e) =>
                      setConsent((prev) => ({ ...prev, marketing: e.target.checked }))
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Marketing Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Used to deliver relevant ads and track campaign performance
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </Box>
          </Collapse>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 1.5 },
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              variant="outlined"
              size={isMobile ? 'medium' : 'large'}
              onClick={handleRejectAll}
              sx={{ flex: { sm: 1 }, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
            >
              Reject All
            </Button>
            {expanded && (
              <Button
                variant="outlined"
                size={isMobile ? 'medium' : 'large'}
                onClick={handleAcceptSelected}
                sx={{ flex: { sm: 1 }, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
              >
                Save Preferences
              </Button>
            )}
            <Button
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              onClick={handleAcceptAll}
              sx={{ flex: { sm: 1 }, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
            >
              Accept All
            </Button>
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
}
